import api from '@core/services/api/_api';
import ENUMS from '@core/classes/Enums';
import FIMenuCustomer from '@core/classes/FIMenuCustomer';
import { getDateInstance } from '@core/services/util.date';
import VersionsLender from '@core/classes/LenderVersionClass';

const DropdownHelper = {
	/// Label will say "Default" when no state is given
	getLenders: async (): Promise<VersionsLender[]> => {
		return await DropdownHelper.$cache.getSet('GET_LENDERS', async () => {
			const apiResponse = await api.settings.getLenders();
			return apiResponse && apiResponse.data ? apiResponse.data : null;
		});
	},

	getDealTypes: async () => {
		return await DropdownHelper.$cache.getSet('GET_DEAL_TYPES', async () => {
			return ['FINANCE', 'LEASE', 'CASH'].map(c => ({
				value: c,
				display: c,
			}));
		});
	},

	getInventoryTypes: async () => {
		return await DropdownHelper.$cache.getSet('GET_INVENTORY_TYPES', async () => ['New', 'Used', 'Certified']);
	},

	getBrands: async (forceRefresh = false) => {
		return await DropdownHelper.$cache.getSet(
			'GET_BRANDS_DROPDOWN',
			async () => {
				let data = null;
				const apiResponse = await api.settings.getBrands();
				if (apiResponse && apiResponse.data) {
					data = apiResponse.data.map(c => {
						return {
							value: c,
							display: c.brandName,
						};
					});
				}
				return data;
			},
			forceRefresh,
		);
	},
	getModels: async (forceRefresh = false) => {
		return await DropdownHelper.$cache.getSet(
			'GET_MODELS_DROPDOWN',
			async () => {
				let data = null;
				const apiResponse = await api.settings.getModels();
				if (apiResponse && apiResponse.data) {
					data = apiResponse.data.map(c => {
						return {
							value: c,
							display: c.modelName,
						};
					});
				}
				return data;
			},
			forceRefresh,
		);
	},
	getCarCalcsTitleIdsDropDown: async () => {
		return await DropdownHelper.$cache.getSet('GET_CARCALCS_TITLEIDS_DROPDOWN', async () => {
			let dropdownData = null;
			const apiResponse = await api.utilities.getCarCalcsTitleIds();
			console.log(apiResponse);
			if (apiResponse && apiResponse.data) {
				dropdownData = apiResponse.data.map((c: any) => {
					return {
						value: c.titleId,
						display: `${c.titleId} - ${c.name}${c.alternativeNames ? ' - ' + c.alternativeNames : ''}`,
					};
				});
			}
			return dropdownData;
		});
	},
	getAdditionTypes: () => {
		return ENUMS.ADDITION_TYPES.toList();
	},
	getValidationListForCBCList: () => {
		return ["Driver's License", 'Passport', 'Social Security Card', 'Vehicle Title/Reg', 'Utility Bill', 'Credit Thawed/Unfrozen', 'Other'];
	},

	getValidationListForDOB: () => {
		return ["Driver's License", 'Passport', 'Other'];
	},
	getValidationListForSSN: () => {
		return ['Passport', 'Social Security Card', 'Other'];
	},
	getValidationListForAddress: () => {
		return ["Driver's License", 'Passport', 'Vehicle Title/Reg', 'Utility Bill', 'Other'];
	},
	getValidationListForFraud: () => {
		return ["Driver's License", 'Passport', 'Social Security Card', 'Credit Thawed/Unfrozen', 'Other'];
	},

	getRouteOneTaxesList: () => {
		return [
			'luxuryTax',
			'salesTax',
			'totalSalesUseTax',
			'securityDepositTax',
			'capCostReductionTax',
			'monthlyUseTax',
			'documentStampTax',
			'motorVehicleTax',
			'upfrontFeeTax',
			'vehicleInventoryTax',
			'exciseTax',
			'wheelTax',
			'localOptionSalesTax',
			'localOption',
			'countyTax',
			'taxNotIncludedInCashPrice',
			'taxNotIncludedInCashPrice2',
			'taxNotIncludedInCashPrice3',
			'singleArticleTax',
			'propertyTax',
			'otherTax',
			'titleAdValoremTax',
			'grossReceiptsTax',
			'serviceContractTax',
		];
	},
	getRouteOneFeesList: () => {
		return [
			'acquisitionFee',
			'administrationFee',
			'arbitrationFee',
			'batteryFee',
			'convenienceFee',
			'dealerProcessingFee',
			'deliveryHandlingFee',
			'deputyServicFeePaidToDealer',
			'deqServiceFee',
			'documentFee',
			'electronicFilingFee',
			'emissionInspectionFee',
			'filingFee',
			'lenderMemberFee',
			'licenseFee',
			'lienNotationFee',
			'motorVehicleInspectionFee',
			'officialFees',
			'otherFees',
			'plateFee',
			'processingFee',
			'registrationFee',
			'safetyInspectionFee',
			'securityInterestChargesFilingFee',
			'serviceHandlingFee',
			'smogCertificationPaidToSeller',
			'smogCertificationPaidToState',
			'smogExpemptionDonatonFee',
			'suplamentalTitleFee',
			'tireFee',
			'titleRegistrationProcessingFee',
			'titleFee',
			'transferFee',
			'officialFees2',
			'officialFees3',
			'dispositionFee',
			'ERTFee',
			'messengerFee',
			'motorVehicleWarrantyTrustFundFee',
			'notaryFee',
			'purchaseOptionFee',
			'rentalSurchargeFee',
			'temporaryTagFee',
			'totalFees',
			'freightFee',
			'georgiaMotorVehicleWarrantyRightsActFee',
			'loanOriginationFee',
			'nonFilingInsuranceFee',
			'onlineRegistrationFee',
			'roadAndBridgeFee',
			'buyersTagFee',
			'childSafetySeatFee',
			'emergencyMedicalServiceAdministrationFee',
			'exemptionFee',
			'infrastructureMaintenanceFee',
			'localFee',
			'electronicTempTagProcessingFee',
			'uDriveItFee',
			'airQualityFee',
			'courtesyDeliveryFee',
			'exemptionFee',
			'titlePrepFee',
			'monthlyLocalFee',
			'motorVehicleInspectionFeePaidToStation',
		];
	},
	getDocumentPacketCategories: async () => {
		return await DropdownHelper.$cache.getSet(
			'DOCUMENT_PACKET_CATEGORIES',
			async () => {
				let data = null;

				const response = await api.utilities.getPaperworkCategories();
				const paperworkCategories = response.data.sort((a: any, b: any) => a.orderIndex - b.orderIndex);

				if (paperworkCategories) {
					data = paperworkCategories.map((c: any) => {
						return {
							value: c,
							display: c.description,
						};
					});
				}
				return data;
			},
			true,
		);
	},
	getProfitFlags: () => {
		return ENUMS.PROFIT_FLAGS.toList();
	},
	getVehicleDeliveryMethods: () => {
		return ENUMS.VEHICLE_DELIVERY_METHODS.toList();
	},
	$cache: {
		getSet: async (CACHE_KEY: any, dataFunction: any, forceRefresh = false) => {
			//CHECK IF WE HAVE IT IN CACHE
			let dropdownData = null;
			if (forceRefresh == false) {
				const dropdownData = DropdownHelper.$cache.get(CACHE_KEY) ?? null;

				if (dropdownData) {
					return dropdownData;
				}
			}

			//CALL THE SERVER - GET NEW DATA
			dropdownData = await dataFunction();
			if (dropdownData) {
				//SAVE TO CACHE
				DropdownHelper.$cache.set(CACHE_KEY, dropdownData);
			}

			return dropdownData;
		},
		get: (key: any) => {
			const currDate = getDateInstance();
			const cachedItem = (DropdownHelper.$cache.items as any)[key] ?? null;
			if (cachedItem && cachedItem.exp > currDate) return cachedItem.data;
			else return null;
		},
		set: (key: any, data: any, expirationInMinutes = 30) => {
			const expirationDate = getDateInstance();
			const newExpDate = expirationDate.setTime(expirationDate.getTime() + expirationInMinutes * 60 * 1000);

			const newCacheItem = {
				key: key,
				data: data,
				exp: newExpDate,
			};

			(DropdownHelper.$cache.items as any)[key] = newCacheItem;
		},
		items: {},
	},
};

/************************************************************** */
/********* Made outside of the DropdownHelper object ********** */
/********** so you can use it in any file ********************* */
/********* without importing the DropdownHelper object ********* */
/************************************************************** */

/**
 * Flags the out the deal types that are not allowed for the current deaL as disabled, with a tooltip message to explain why.
 * If any of the customers on the deal do not have a customerApplicationStatus of Completed, Finance and Lease are not allowed.
 * @param {any} deal - The deal to check.
 * @returns {any[]} An array of the allowed deal types for the current user
 */
export function getAllowedDealTypes(deal: any) {
	const allowedDealTypes = ENUMS.DEAL_TYPES.toList();
	const customers = [deal.customer, deal.coCustomer];
	//if any of the customers have a customerApplicationStatus of NOT completed, then Finance and Lease deals are not allowed. Cash IS allowed, not disabled.
	// only check if there is a customer id, if not, then we don't need to check the customerApplicationStatus.
	// or if it's a business customer, then we don't need to check the customerApplicationStatus.
	const hasMissingInformation = customers
		?.filter(customer => customer.id != null)
		.some((customer: FIMenuCustomer) => !customer.isEntity && customer.customerApplicationStatus !== ENUMS.CUSTOMER_APPLICATION_STATUS.Completed);

	// Check if the primary usage is restricted (Wholesale, Service Loaner, Dealer Trade, or Accessory Sale)
	const hasRestrictedPrimaryUsage = deal.isRestrictedPrimaryUsage();

	if (!hasMissingInformation && !hasRestrictedPrimaryUsage) {
		return allowedDealTypes;
	}

	const missingInfoTooltip = 'Please have customer(s) complete housing and employment information to select this option.';
	const restrictedPrimaryUsageTooltip = 'Finance and Lease are not available for Wholesale, Service Loaner, Dealer Trade, or Accessory Sale deals.';

	return allowedDealTypes.map((dealType: any) => {
		const baseProps = {
			display: dealType.display,
			value: dealType.value,
		};

		if (dealType.value === ENUMS.DEAL_TYPES.FINANCE || dealType.value === ENUMS.DEAL_TYPES.LEASE) {
			// Determine the appropriate tooltip message
			let tooltipMessage = '';
			if (hasRestrictedPrimaryUsage) {
				tooltipMessage = restrictedPrimaryUsageTooltip;
			} else if (hasMissingInformation) {
				tooltipMessage = missingInfoTooltip;
			}

			return {
				...dealType,
				...baseProps,
				disabled: true,
				tooltip: tooltipMessage,
			};
		}

		return {
			...dealType,
			...baseProps,
		};
	});
}

export default DropdownHelper;
